import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, Upload, Loader2, BarChart3, Sparkles, X } from 'lucide-react';

interface UploadPdfModalProps {
  isOpen: boolean;
  uploadingFile: File | null;
  setUploadingFile: (file: File | null) => void;
  isUploading: boolean;
  onUpload: () => void;
  onClose: () => void;
}

export function UploadPdfModal({
  isOpen,
  uploadingFile,
  setUploadingFile,
  isUploading,
  onUpload,
  onClose,
}: UploadPdfModalProps) {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setUploadingFile(acceptedFiles[0]);
    }
  }, [setUploadingFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024,
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Enhanced Backdrop */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/40 to-black/50 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Enhanced Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden animate-in">
        {/* Enhanced Header */}
        <div className="relative bg-gradient-to-r from-[#1e73be] via-[#4A90C8] to-[#5BA5D8] px-8 py-6">
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full blur-3xl -translate-x-16 -translate-y-16" />
            <div className="absolute bottom-0 right-0 w-48 h-48 bg-white rounded-full blur-3xl translate-x-24 translate-y-24" />
          </div>

          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/30 backdrop-blur-sm rounded-2xl shadow-lg">
                <Sparkles className="w-7 h-7 text-white drop-shadow-md" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-white drop-shadow-md">Analyze PDF</h2>
                <p className="text-sm text-white/90 font-medium mt-0.5">AI-powered content extraction</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-all duration-200 hover:scale-110"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-8 space-y-6 bg-gradient-to-br from-gray-50 to-white">
          {/* Enhanced Drop zone */}
          <div
            {...getRootProps()}
            className={`relative border-3 border-dashed rounded-3xl p-10 text-center cursor-pointer transition-all duration-300 overflow-hidden ${
              isDragActive
                ? 'border-[#1e73be] bg-gradient-to-br from-blue-50 to-blue-100/50 scale-[1.02] shadow-xl'
                : uploadingFile
                ? 'border-green-400 bg-gradient-to-br from-green-50 to-emerald-50 shadow-lg'
                : 'border-gray-300 hover:border-[#1e73be] hover:bg-gradient-to-br hover:from-blue-50/50 hover:to-purple-50/30 hover:shadow-lg'
            }`}
          >
            <input {...getInputProps()} />
            {uploadingFile ? (
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl shadow-lg">
                  <FileText className="w-10 h-10 text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-lg">{uploadingFile.name}</p>
                  <p className="text-sm text-gray-600 mt-1 font-semibold">
                    {(uploadingFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setUploadingFile(null);
                  }}
                  className="px-4 py-2 text-sm text-red-600 hover:text-white bg-red-50 hover:bg-red-500 font-semibold rounded-xl transition-all duration-200 border-2 border-red-200 hover:border-red-500"
                >
                  Remove File
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4">
                <div className={`p-5 rounded-2xl transition-all duration-300 ${
                  isDragActive
                    ? 'bg-gradient-to-br from-[#1e73be] to-[#4A90C8] shadow-xl scale-110'
                    : 'bg-gradient-to-br from-gray-100 to-gray-200'
                }`}>
                  <Upload className={`w-12 h-12 transition-colors ${
                    isDragActive ? 'text-white' : 'text-gray-400'
                  }`} />
                </div>
                <div>
                  <p className={`font-bold text-lg transition-colors ${
                    isDragActive ? 'text-[#1e73be]' : 'text-gray-700'
                  }`}>
                    {isDragActive ? 'Drop your PDF here' : 'Drag & drop a PDF file'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1.5 font-medium">
                    or click to browse (max 50 MB)
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Actions */}
          <div className="flex items-center gap-4 pt-4">
            <button
              onClick={onClose}
              className="flex-1 py-3.5 rounded-2xl font-bold text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 transition-all duration-200 border-2 border-gray-300 hover:border-gray-400"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              onClick={onUpload}
              disabled={!uploadingFile || isUploading}
              className="flex-1 py-3.5 rounded-2xl font-bold text-sm bg-gradient-to-r from-[#ea7500] to-[#f59e42] hover:from-[#ea7500]/90 hover:to-[#f59e42]/90 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg border-2 border-orange-600/20 hover:-translate-y-0.5"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <BarChart3 className="w-5 h-5" />
                  Start Analysis
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
